/**
 * Sprint D — MaintenanceGate
 *
 * Wraps the entire route tree. When `VITE_MAINTENANCE_MODE === "true"` is set
 * in the deployment environment, every route renders the Maintenance page
 * instead of its normal content.
 *
 * Toggle via Vercel env var:
 *   VITE_MAINTENANCE_MODE=true   # block all UI
 *   VITE_MAINTENANCE_MODE=false  # normal operation (or unset)
 *
 * The /maintenance route remains directly reachable (via direct navigation or
 * a fixed admin bookmark) so Rickard can verify the page renders before
 * flipping the global flag.
 *
 * Edge-function writes are blocked separately via supabase/functions/_shared/
 * maintenance.ts (reads MAINTENANCE_MODE from Deno.env). Both sides should be
 * toggled together for the Sunday window:
 *   - Vercel:   VITE_MAINTENANCE_MODE=true
 *   - Supabase: MAINTENANCE_MODE=true (Project → Settings → Edge Functions → Secrets)
 */

import Maintenance from "@/pages/Maintenance";

const FLAG_VALUES_TRUE = new Set(["true", "1", "yes", "on"]);

/**
 * Read the maintenance flag at call time.
 *
 * We read both `import.meta.env` (Vite production path) and `process.env`
 * (vitest test path) so that `vi.stubEnv` works in unit tests. In production
 * `process.env` is undefined in browser context — Vite replaces such reads
 * with an empty object, so this is harmless.
 *
 * Exported for testability.
 */
export function isMaintenanceModeActive(): boolean {
  const fromImportMeta = import.meta.env?.VITE_MAINTENANCE_MODE;
  // UPPDRAG-30: process is a Node global; guarded `typeof` check at runtime
  // but ESLint's no-undef can't see the guard. Disable for this single line.
  // eslint-disable-next-line no-undef
  const fromProcess = typeof process !== "undefined" ? process.env?.VITE_MAINTENANCE_MODE : undefined;
  const raw = fromImportMeta ?? fromProcess;
  if (typeof raw !== "string") return false;
  return FLAG_VALUES_TRUE.has(raw.trim().toLowerCase());
}

interface Props {
  children: React.ReactNode;
}

/**
 * Wraps children. Renders Maintenance page when flag is on; otherwise passes
 * through.
 */
export function MaintenanceGate({ children }: Props) {
  if (isMaintenanceModeActive()) {
    return <Maintenance />;
  }
  return <>{children}</>;
}
